// src/components/ThumbnailGenerator/hooks/useThumbnailGeneration.ts
import { useState, useCallback } from 'react';
import { 
  generateThumbnailFromDescription,
  generateThumbnailFromCaptions,
  ThumbnailGenerationResult
} from '../../../services/thumbnailGenerationApi';

export interface GenerationState {
  isLoading: boolean;
  progress: number;
  currentStep: string;
  error: string | null;
  result: ThumbnailGenerationResult | null;
  status: 'idle' | 'uploading' | 'processing' | 'generating' | 'completed' | 'error';
}

export const useThumbnailGeneration = () => {
  const [state, setState] = useState<GenerationState>({
    isLoading: false,
    progress: 0,
    currentStep: '',
    error: null,
    result: null,
    status: 'idle'
  });

  const updateState = useCallback((updates: Partial<GenerationState>) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  const generateFromDescription = useCallback(async (
    description: string,
    options?: any
  ): Promise<ThumbnailGenerationResult | null> => {
    try {
      console.log('Starting thumbnail generation with description:', description);
      
      updateState({
        isLoading: true,
        status: 'generating',
        currentStep: 'Generating thumbnail from description...',
        progress: 25,
        error: null
      });

      const result = await generateThumbnailFromDescription(description, options);
      
      console.log('Thumbnail generation API result:', result);
      
      updateState({
        isLoading: false,
        status: 'completed',
        currentStep: 'Thumbnail generated successfully!',
        progress: 100,
        result
      });

      console.log('Returning result from hook:', result);
      return result;
    } catch (error) {
      console.error('Thumbnail generation error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate thumbnail';
      updateState({
        isLoading: false,
        status: 'error',
        error: errorMessage,
        progress: 0
      });
      return null;
    }
  }, [updateState]);

  const generateFromCaptions = useCallback(async (
    projectId: string,
    options?: any
  ): Promise<ThumbnailGenerationResult | null> => {
    try {
      updateState({
        isLoading: true,
        status: 'processing',
        currentStep: 'Processing existing captions...',
        progress: 25,
        error: null
      });

      updateState({
        status: 'generating',
        currentStep: 'Generating thumbnail from captions...',
        progress: 75
      });

      const result = await generateThumbnailFromCaptions(projectId, options);
      
      updateState({
        isLoading: false,
        status: 'completed',
        currentStep: 'Thumbnail generated successfully!',
        progress: 100,
        result
      });

      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate from captions';
      updateState({
        isLoading: false,
        status: 'error',
        error: errorMessage,
        progress: 0
      });
      return null;
    }
  }, [updateState]);

  const reset = useCallback(() => {
    setState({
      isLoading: false,
      progress: 0,
      currentStep: '',
      error: null,
      result: null,
      status: 'idle'
    });
  }, []);

  return {
    ...state,
    generateFromDescription,
    generateFromCaptions,
    reset,
    updateState
  };
};